import { useCallback, useMemo } from 'react';
import { Platform } from 'react-native';
import { useActionSheet } from '@expo/react-native-action-sheet';
import * as ImagePicker from 'expo-image-picker';
import Toast from 'react-native-root-toast';

import { useTheme } from '../../theme';

const IMAGE_PICKER_OPTIONS = { mediaTypes: ['images'] };
const PHOTO_SOURCE = {
	CAMERA: 'camera',
	LIBRARY: 'library',
};

// Single-photo picker for people uploads (document uploads, incident attachments):
// asks camera vs library via the action sheet and resolves with one image asset or null.
const usePeoplePhotoPicker = () => {
	const { showActionSheetWithOptions } = useActionSheet();
	const { theme } = useTheme();

	const { colors, type } = theme.ui;
	const actionSheetStyles = useMemo(() => ({
		containerStyle: {
			backgroundColor: colors.surface,
		},
		textStyle: {
			...type.body,
			color: colors.text,
		},
		tintColor: colors.text,
	}), [colors, type]);

	const launchPicker = useCallback(async (launchFunction, photoSource) => {
		if (photoSource === PHOTO_SOURCE.CAMERA) {
			const { status: cameraPermissionStatus } = await ImagePicker.requestCameraPermissionsAsync();
			if (cameraPermissionStatus !== 'granted') {
				Toast.show('Camera permission is required');
				return null;
			}
		}
		else if (Platform.OS === 'ios') {
			const { status: libraryPermissionStatus } = await ImagePicker.requestMediaLibraryPermissionsAsync();
			if (libraryPermissionStatus !== 'granted') {
				Toast.show('Photo library permission is required');
				return null;
			}
		}

		let result = null;
		try {
			result = await launchFunction(IMAGE_PICKER_OPTIONS);
		}
		catch (error) {
			Toast.show('Unable to open the photo picker');
			return null;
		}

		if (result?.canceled || result?.cancelled) {
			return null;
		}

		const assets = Array.isArray(result?.assets) ? result.assets : (result?.uri ? [result] : []);
		return assets[0] || null;
	}, []);

	const pickPhoto = useCallback(() => new Promise((resolve) => {
		showActionSheetWithOptions(
			{
				options: ['Take photo', 'Choose from library', 'Cancel'],
				cancelButtonIndex: 2,
				...actionSheetStyles,
			},
			async (buttonIndex) => {
				if (buttonIndex === 2 || buttonIndex === undefined) {
					resolve(null);
					return;
				}

				const photoSource = buttonIndex === 0 ? PHOTO_SOURCE.CAMERA : PHOTO_SOURCE.LIBRARY;
				const launchFunction = photoSource === PHOTO_SOURCE.CAMERA
					? ImagePicker.launchCameraAsync
					: ImagePicker.launchImageLibraryAsync;
				resolve(await launchPicker(launchFunction, photoSource));
			},
		);
	}), [actionSheetStyles, launchPicker, showActionSheetWithOptions]);

	return { pickPhoto };
};

export default usePeoplePhotoPicker;
